/**
 * Global stream status polling service
 * Runs independently of React component lifecycle to ensure reliable status checks
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || '';

type StatusCallback = (status: string) => void;

class StreamStatusPoller {
  private intervalId: NodeJS.Timeout | null = null;
  private streamId: string | null = null;
  private callback: StatusCallback | null = null;
  private isPolling: boolean = false;

  /**
   * Start polling for stream status changes
   */
  startPolling(streamId: string, callback: StatusCallback) {
    logger.debug('[Global Poller] Starting polling for stream', { streamId });

    // Stop any existing polling first
    this.stopPolling();

    this.streamId = streamId;
    this.callback = callback;
    this.isPolling = true;

    // Run initial check immediately
    this.checkStatus();

    // Then check every 3 seconds
    this.intervalId = setInterval(() => {
      this.checkStatus();
    }, 3000);

    logger.debug('[Global Poller] Polling started, interval set');
  }

  /**
   * Stop polling
   */
  stopPolling() {
    logger.debug('[Global Poller] Stopping polling');

    this.isPolling = false;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.streamId = null;
    this.callback = null;
  }

  /**
   * Check stream status
   */
  private async checkStatus() {
    if (!this.isPolling || !this.streamId || !this.callback) {
      logger.debug('[Global Poller] Skipping check - not actively polling');
      return;
    }

    logger.debug('[Global Poller] Checking status', { timestamp: new Date().toISOString() });

    try {
      const url = `${API_URL}/api/live-streams/${this.streamId}`;
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        logger.error('[Global Poller] No auth token available');
        return;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          logger.debug('[Global Poller] Stream not found (404)');
          return;
        }
        logger.error('[Global Poller] Failed to fetch status', undefined, { status: response.status });
        return;
      }

      const responseData = await response.json();
      const data = responseData.data || responseData;

      logger.debug('[Global Poller] Stream status', { status: data.status });

      // Notify callback of status change
      if (data.status === 'stopped' || data.status === 'idle') {
        logger.debug('[Global Poller] Stream is stopped, triggering callback');
        this.callback(data.status);
      }
    } catch (err) {
      logger.error('[Global Poller] Error checking status', err);
    }
  }

  /**
   * Check if currently polling
   */
  isActive(): boolean {
    return this.isPolling;
  }
}

// Export singleton instance
export const streamStatusPoller = new StreamStatusPoller();
